import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { fileStamp, fmtDate, formatMoney } from './format';
import { COMPANY_NAME } from '../config';

const toArray = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (data.data && Array.isArray(data.data)) return data.data;
  if (data.logs && Array.isArray(data.logs)) return data.logs;
  if (data.invoices && Array.isArray(data.invoices)) return data.invoices;
  if (data.attendance && Array.isArray(data.attendance)) return data.attendance;
  if (data.machines && Array.isArray(data.machines)) return data.machines;
  if (data.items && Array.isArray(data.items)) return data.items;
  if (data.rows && Array.isArray(data.rows)) return data.rows;
  if (data.grouped && Array.isArray(data.grouped)) return data.grouped;
  if (data.entries && Array.isArray(data.entries)) return data.entries;
  if (data.summary && data.summary.data) return data.summary.data;
  if (data.payrolls && Array.isArray(data.payrolls)) return data.payrolls;
  if (data.payments && Array.isArray(data.payments)) return data.payments;
  return [];
};

const pick = (obj, keys) => {
  const out = {};
  keys.forEach((k) => {
    if (obj && k in obj) out[k] = obj[k];
  });
  return out;
};

export const exportToExcel = (rows, reportName, columns, range = null, mainHeading = null, summary = null, accountTitle = null) => {
  const list = Array.isArray(rows) ? rows : toArray(rows);
  const fmtVal = (k, v) => (v && typeof v === 'string' && /date/i.test(k) ? fmtDate(v) : v);

  let keys;
  let aoa;
  if (columns && columns.length) {
    keys = columns.map((c) => c.key);
    aoa = [
      columns.map((c) => c.label || c.key),
      ...list.map((r) => columns.map((c) => fmtVal(c.key, r[c.key]))),
    ];
  } else {
    keys = list.length ? Object.keys(list[0]) : [];
    aoa = [keys, ...list.map((r) => keys.map((k) => fmtVal(k, r[k])))];
  }
  if (summary) {
    const totalRow = keys.map((k) => {
      if (k === 'date') return 'TOTAL';
      if (k === 'debit') return summary.total_debit || 0;
      if (k === 'credit') return summary.total_credit || 0;
      if (k === 'balance') return summary.closing_balance || 0;
      return '';
    });
    aoa.push(totalRow);
  }
  if (range) {
    const fromTo = `From: ${range.from ? fmtDate(range.from) : '-'}   To: ${range.to ? fmtDate(range.to) : '-'}`;
    if (reportName === 'ledger_report') {
      const opening = summary ? `Opening Balance: ${formatMoney(summary.opening_balance)}` : '';
      const account = accountTitle ? `Account: ${accountTitle}` : '';
      aoa = [[mainHeading || COMPANY_NAME, account, opening], ['Ledger Report'], [fromTo], [], ...aoa];
    } else {
      aoa = [[fromTo], [], ...aoa];
    }
  }

  const worksheet = XLSX.utils.aoa_to_sheet(aoa);
  const maxWidth = (v) =>
    String(v == null ? '' : v).length * 9 > 40 ? Math.min(String(v).length * 9, 60) : 40;
  if (keys.length) {
    worksheet['!cols'] = keys.map((k) => ({
      wch: Math.max(...[k, ...list.map((r) => r[k])].map(maxWidth)),
    }));
  }

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Report');
  XLSX.writeFile(workbook, `${reportName}_${fileStamp()}.xlsx`);
};

export const exportInvoiceSummaryExcel = (rows, reportName) => {
  const list = Array.isArray(rows) ? rows : toArray(rows);
  const headers = [
    'invoice Date', 'Invoice Type', 'Buyer NTN CNIC', 'Invoice Ref No', 'Hs Code',
    'Product Description', 'UOM', 'Unit Cost', 'Quantity', 'Rate',
    'Value Sales Excluding ST', 'SalesTax Applicable ', 'Further Tax', 'Total Values',
    'Sro Item Serial No', 'Sro Schedule No', 'Sale Type',
  ];

  const body = list.map((r) => [
    fmtDate(r.date) || '', r.invoice_type || '', r.buyer_ntn_cnic || '', r.invoice_no || '', r.hs_code || '',
    r.description || '', r.uom || '', r.unit_cost || 0, r.quantity || 0, r.rate || '',
    r.value_excl_st || 0, r.sales_tax || 0, r.further_tax || '', r.total_value || 0,
    r.sro_item_serial_no || '', r.sro_schedule_no || '', r.sale_type || '',
  ]);

  const totalQty = list.reduce((s, r) => s + Number(r.quantity || 0), 0);
  const totalValue = list.reduce((s, r) => s + Number(r.value_excl_st || 0), 0);
  const totalTax = list.reduce((s, r) => s + Number(r.sales_tax || 0), 0);
  const totalAll = list.reduce((s, r) => s + Number(r.total_value || 0), 0);
  const totalRow = ['', '', '', '', '', '', '', '', totalQty, '', totalValue, totalTax, '', totalAll, '', '', ''];

  const worksheet = XLSX.utils.aoa_to_sheet([headers, ...body, totalRow]);
  worksheet['!cols'] = headers.map((h, i) => ({
    wch: i === 5 ? 30 : 18,
  }));

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Invoice Summary');
  XLSX.writeFile(workbook, `${reportName}_${fileStamp()}.xlsx`);
};

// Invoice Summary report — matches client Excel layout (RptInvSummery) with a Total row.
export const exportInvSummaryExcel = (rows, reportName) => {
  const headers = [
    'Inv #', 'Party Name', 'Inv Type', 'Date', 'Sales Tax', 'Sales Tax %',
    'Excluding Amount', 'Quantity', 'Including Amount', 'NTN #',
  ];
  const body = rows.map((r) => [
    r.invoice_no || '',
    r.party_name || '',
    r.invoice_type || '',
    fmtDate(r.date) || '',
    r.sales_tax_desc || '',
    r.tax_rate || '',
    Number(r.excl_amount || 0),
    Number(r.quantity || 0),
    Number(r.incl_amount || 0),
    r.ntn_no || '',
  ]);
  const totalRow = [
    'Total', '', '', '', '', '',
    rows.reduce((s, r) => s + Number(r.excl_amount || 0), 0),
    rows.reduce((s, r) => s + Number(r.quantity || 0), 0),
    rows.reduce((s, r) => s + Number(r.incl_amount || 0), 0),
    '',
  ];

  const worksheet = XLSX.utils.aoa_to_sheet([headers, ...body, totalRow]);
  worksheet['!cols'] = headers.map((h, i) => ({ wch: i === 1 || i === 4 ? 32 : 16 }));

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Invoice Summary');
  XLSX.writeFile(workbook, `${reportName}_${fileStamp()}.xlsx`);
};

const amount = (v) => Number(v || 0);

// Money format for PDFs: 1,500,000 / 357,888 — decimals only when fractional.
const fmtAmt = formatMoney;

// Report PDF cells: numbers get comma format, narration/description show UPPERCASE.
const UPPER_CELL_KEYS = ['narration', 'description', 'notes', 'purpose'];
const pdfCell = (key, v) => {
  if (typeof v === 'number') return fmtAmt(v);
  if (UPPER_CELL_KEYS.includes(key) && typeof v === 'string') return v.toUpperCase();
  if (typeof v === 'string' && v && /date/i.test(key)) return fmtDate(v);
  return v;
};

const numberToWords = (num) => {
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const scales = ['', 'Thousand', 'Million', 'Billion'];

  const twoDigits = (n) => {
    if (n < 20) return ones[n];
    return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
  };

  const threeDigits = (n) => {
    const hundred = Math.floor(n / 100);
    const rest = n % 100;
    let out = '';
    if (hundred) out += ones[hundred] + ' Hundred';
    if (hundred && rest) out += ' ';
    if (rest) out += twoDigits(rest);
    return out;
  };

  if (num === 0) return 'Zero';

  let whole = Math.floor(Math.abs(num));
  let output = '';
  let scaleIndex = 0;
  while (whole > 0) {
    const chunk = whole % 1000;
    if (chunk > 0) {
      const chunkText = threeDigits(chunk);
      output = chunkText + (scales[scaleIndex] ? ' ' + scales[scaleIndex] : '') + (output ? ' ' + output : '');
    }
    whole = Math.floor(whole / 1000);
    scaleIndex++;
  }

  if (num < 0) output = 'Minus ' + output;

  const paise = Math.round((Math.abs(num) - Math.floor(Math.abs(num))) * 100);
  if (paise > 0) {
    output += ' and ' + twoDigits(paise) + ' Paisa';
  }

  return output + ' Only';
};

const loadLogo = (base64) => new Promise((resolve) => {
  if (!base64) return resolve(null);
  const img = new Image();
  img.onload = () => resolve({ data: base64, w: img.width, h: img.height });
  img.onerror = () => resolve(null);
  img.src = base64;
});

const hasVal = (v) => !!(v && String(v).trim());

const addHeader = (doc, pageWidth, margin, s, fallback) => {
  const hasLogo = s && s._logoData;
  const rightX = pageWidth - margin;
  let logoBottomY = 10;

  if (hasLogo && s._logoData) {
    const logo = s._logoData;
    const maxW = 35;
    const maxH = 20;
    const ratio = Math.min(maxW / logo.w, maxH / logo.h);
    const w = logo.w * ratio;
    const h = logo.h * ratio;
    doc.addImage(logo.data, 'PNG', margin, 10, w, h);
    logoBottomY = 10 + h;
  } else {
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(23, 32, 51);
    const headerName = hasVal(s.invoice_header_name) ? s.invoice_header_name : (fallback || '');
    if (hasVal(headerName)) {
      doc.text(String(headerName).trim().toUpperCase(), margin, 18);
    }
    logoBottomY = 18;
  }

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(80, 80, 80);
  let ry = 10;
  if (hasVal(s.invoice_header_phone)) {
    doc.text('Tel: ' + s.invoice_header_phone, rightX, ry, { align: 'right' });
    ry += 5;
  }
  if (hasVal(s.invoice_header_email)) {
    doc.text('Email: ' + s.invoice_header_email, rightX, ry, { align: 'right' });
    ry += 5;
  }
  if (hasVal(s.invoice_header_website)) {
    doc.text('Web: ' + s.invoice_header_website, rightX, ry, { align: 'right' });
    ry += 5;
  }
  if (hasVal(s.invoice_header_gst)) {
    doc.text('GST # ' + s.invoice_header_gst, rightX, ry, { align: 'right' });
    ry += 5;
  }
  if (hasVal(s.invoice_header_ntn)) {
    doc.text('NTN # ' + s.invoice_header_ntn, rightX, ry, { align: 'right' });
    ry += 5;
  }

  const bottomY = Math.max(logoBottomY, ry) + 2;

  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.3);
  doc.line(margin, bottomY, pageWidth - margin, bottomY);

  return bottomY + 5;
};

const addCommissionHeader = (doc, pageWidth, margin, s) => {
  const hasContent = !!(s._logoData || hasVal(s.invoice_header_name) || hasVal(s.invoice_header_phone)
    || hasVal(s.invoice_header_address) || hasVal(s.invoice_header_email));
  if (!hasContent) return 12;
  return addHeader(doc, pageWidth, margin, s, '');
};

const addFooter = (doc, pageWidth, pageHeight, margin, s) => {
  const footerText = s && s.invoice_footer_text;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 100, 100);
  const lines = footerText ? doc.splitTextToSize(footerText, pageWidth - 2 * margin) : [];
  // Separator above the footer text; move it up when footer needs more lines.
  const maxLineY = pageHeight - 7 - Math.max(0, lines.length - 1) * 4;
  const lineY = Math.min(pageHeight - 14, maxLineY);
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.2);
  doc.line(margin, lineY, pageWidth - margin, lineY);

  // Invoice footer at the bottom (replaces the old "computer generated" line)
  lines.forEach((line, i) => {
    doc.text(line, pageWidth / 2, lineY + 4 + i * 4, { align: 'center' });
  });
  doc.setTextColor(40, 40, 40);
};

// Print the generated PDF directly (same design as download) via the browser print dialog.
const printDoc = (doc) => {
  doc.autoPrint();
  try {
    doc.output('dataurlnewwindow');
  } catch (err) {
    window.open(doc.output('dataurlstring'), '_blank');
  }
};

export const invoiceToPDF = async (invoice, companyName, settings = {}, print = false) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 14;
  const inv = invoice || {};
  const isPurchase = inv.invoice_type === 'purchase';
  const isDebit = inv.invoice_type === 'debit';
  const party = (isPurchase || isDebit) ? (inv.supplier || {}) : (inv.party || {});
  const isCommission = inv.invoice_type === 'commission';
  const isTaxFree = inv.invoice_type === 'tax_free';

  const typeKey = inv.invoice_type === 'tax_free' ? 'taxfree' : (inv.invoice_type || 'sale');
  // Tax-free, purchase, commission & debit: use ONLY their own settings — never fall back
  // to sale/company header, logo or footer. Empty settings = plain invoice.
  const strictHeader = isTaxFree || isPurchase || isCommission || isDebit;
  const resolved = strictHeader
    ? {
        ...settings,
        invoice_header_name: isCommission
          ? (settings.commission_header_name || '')
          : (settings[`${typeKey}_header_name`] || ''),
        invoice_header_phone: isCommission
          ? (settings.commission_header_phone || '')
          : (settings[`${typeKey}_header_phone`] || ''),
        invoice_header_address: isCommission
          ? (settings.commission_header_address || '')
          : (settings[`${typeKey}_header_address`] || ''),
        invoice_header_email: isCommission
          ? (settings.commission_header_email || '')
          : (settings[`${typeKey}_header_email`] || ''),
        invoice_header_website: settings[`${typeKey}_header_website`] || '',
        invoice_header_gst: settings[`${typeKey}_header_gst`] || '',
        invoice_header_ntn: settings[`${typeKey}_header_ntn`] || '',
        invoice_logo_base64: settings[`${typeKey}_logo_base64`] || '',
        invoice_footer_text: settings[`${typeKey}_footer_text`] || '',
      }
    : {
        ...settings,
        invoice_header_name: settings[`${typeKey}_header_name`] || settings.sale_header_name || settings.invoice_header_name || companyName,
        invoice_header_phone: settings[`${typeKey}_header_phone`] || settings.sale_header_phone || settings.invoice_header_phone || '',
        invoice_header_address: settings[`${typeKey}_header_address`] || settings.sale_header_address || settings.invoice_header_address || '',
        invoice_header_email: settings[`${typeKey}_header_email`] || settings.sale_header_email || settings.invoice_header_email || '',
        invoice_header_website: settings[`${typeKey}_header_website`] || settings.sale_header_website || settings.invoice_header_website || '',
        invoice_header_gst: settings[`${typeKey}_header_gst`] || settings.sale_header_gst || settings.invoice_header_gst || '',
        invoice_header_ntn: settings[`${typeKey}_header_ntn`] || settings.sale_header_ntn || settings.invoice_header_ntn || '',
        invoice_logo_base64: settings[`${typeKey}_logo_base64`] || settings.sale_logo_base64 || settings.invoice_logo_base64 || '',
        invoice_footer_text: settings[`${typeKey}_footer_text`] || settings.sale_footer_text || settings.invoice_footer_text || '',
      };

  const logoData = await loadLogo(resolved.invoice_logo_base64);
  const enrichedSettings = { ...resolved, _logoData: logoData };

  let startY;
  if (isCommission) {
    startY = addCommissionHeader(doc, pageWidth, margin, enrichedSettings);
  } else if (strictHeader) {
    const s = enrichedSettings;
    const hasHeaderContent = !!(s._logoData || s.invoice_header_name || s.invoice_header_phone
      || s.invoice_header_email || s.invoice_header_website || s.invoice_header_gst || s.invoice_header_ntn);
    // No taxfree/purchase settings configured → skip header block entirely (plain invoice).
    startY = hasHeaderContent ? addHeader(doc, pageWidth, margin, s, '') : 12;
  } else {
    startY = addHeader(doc, pageWidth, margin, enrichedSettings, companyName);
  }

  const titleMap = {
    sale: 'SALE INVOICE',
    purchase: 'PURCHASE INVOICE',
    credit: 'CREDIT NOTE',
    debit: 'DEBIT NOTE',
    tax_free: 'INVOICE',
    commission: 'COMMISSION INVOICE',
  };
  const invoiceTitle = titleMap[inv.invoice_type] || 'INVOICE';
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(23, 32, 51);
  doc.text(invoiceTitle, pageWidth / 2, startY + 14, { align: 'center' });
  startY += 28;

  doc.setTextColor(40, 40, 40);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  let cy = startY + 5;
  if (!isCommission) {
    const maxNameWidth = pageWidth / 2 - margin - 25;
    const nameLines = doc.splitTextToSize(party.name || '-', maxNameWidth);
    doc.text(nameLines, margin, startY);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    cy = startY + nameLines.length * 5 + 2;
    if (party.ntn_no) {
      doc.setTextColor(120, 120, 120);
      doc.text('NTN:', margin, cy);
      doc.setTextColor(0, 0, 0);
      doc.text(' ' + party.ntn_no, margin + 18, cy);
      cy += 6;
    }
    if (party.gst_no) {
      doc.setTextColor(120, 120, 120);
      doc.text('GST:', margin, cy);
      doc.setTextColor(0, 0, 0);
      doc.text(' ' + party.gst_no, margin + 18, cy);
      cy += 6;
    }
    if (party.cnic) {
      doc.setTextColor(120, 120, 120);
      doc.text('CNIC:', margin, cy);
      doc.setTextColor(0, 0, 0);
      doc.text(' ' + party.cnic, margin + 18, cy);
      cy += 6;
    }
    if (party.address) {
      doc.setTextColor(120, 120, 120);
      doc.text('Address:', margin, cy);
      doc.setTextColor(0, 0, 0);
      const maxAddrWidth = pageWidth / 2 - margin - 43;
      const addrLines = doc.splitTextToSize(' ' + party.address, maxAddrWidth);
      doc.text(addrLines, margin + 18, cy);
      cy += addrLines.length * 5 + 1;
    }
  }

  const boxH = cy - startY + 2;

  const rightX = pageWidth - margin - 3;
  const rightLabels = [
    ['Invoice No:', (isPurchase && inv.supplier_invoice_no ? inv.supplier_invoice_no : inv.invoice_no) || '-'],
    ['Date:', inv.date ? fmtDate(inv.date) : '-'],
  ];
  if (inv.invoice_type === 'sale' && inv.hs_code) {
    rightLabels.push(['HS Code:', inv.hs_code]);
  }
  const labelX = pageWidth / 2 + 25;
  const valueX = pageWidth / 2 + 65;
  let ry2 = startY + 4;
  if (!isCommission) {
    rightLabels.forEach(([k, v]) => {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(120, 120, 120);
      doc.text(k, labelX, ry2);
      doc.setTextColor(0, 0, 0);
      doc.text(v, valueX, ry2);
      ry2 += 6;
    });
  }

  const tableStartY = startY + boxH + 4;
  let contentEndY = null;

  if (isCommission) {
    // Form-style: small label on top, bold value below. No tables.
    const col2W = (pageWidth - 2 * margin) / 2;
    const col3W = (pageWidth - 2 * margin) / 3;
    const col2X = margin + col2W;
    const col3X = margin + col3W;
    const col3bX = margin + 2 * col3W;
    let y = tableStartY + 4;

    const lbl = (t, x) => {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(140, 140, 140);
      doc.text(String(t).toUpperCase(), x, y);
    };
    const val = (t, x, size = 11) => {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(size);
      doc.setTextColor(20, 20, 20);
      doc.text(String(t === undefined || t === null || t === '' ? '-' : t), x, y + 6);
    };

    const blockValue = (lines, size = 11) => {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(size);
      doc.setTextColor(20, 20, 20);
      doc.text(lines, margin, y + 6);
      y += 6 + lines.length * 5 + 5;
    };

    lbl('Company', margin);
    blockValue(doc.splitTextToSize(party.name || '-', pageWidth - 2 * margin));

    const invoiceAddr = inv.supplier_address || party.address;
    if (invoiceAddr) {
      lbl('Address', margin);
      blockValue(doc.splitTextToSize(invoiceAddr, pageWidth - 2 * margin));
    }

    const suppName = (inv.supplier && inv.supplier.name) || '';
    if (suppName) {
      lbl('Supplier', margin);
      blockValue(doc.splitTextToSize(suppName, pageWidth - 2 * margin));
    }

    lbl('Invoice #', margin);
    lbl('Date', col2X);
    val(inv.invoice_no || '-', margin);
    val(inv.date ? fmtDate(inv.date) : '-', col2X);
    y += 16;

    lbl('Sales Contract', margin);
    lbl('Container #', col2X);
    val(inv.sales_contract || '-', margin);
    val(inv.container_no || '-', col2X);
    y += 16;

    lbl('Description / Purpose', margin);
    val(String(inv.purpose || 'Commission').toUpperCase(), margin);
    y += 16;

    const totalUSD = inv.invoice_amount_usd || amount(inv.total);
    const totalPKR = inv.total_pkr || (Number(inv.net_weight) && Number(inv.commission_rate) && Number(inv.usd_rate)
      ? Number(inv.net_weight) * Number(inv.commission_rate) * Number(inv.usd_rate)
      : totalUSD * amount(inv.usd_rate));

    lbl('Net Weight (Kg)', margin);
    lbl('US Dollar Rate', col3X);
    lbl('Invoice Amount in US$', col3bX);
    val(inv.net_weight, margin);
    val(inv.usd_rate ? 'PKR ' + Number(inv.usd_rate).toLocaleString() : '-', col3X, 10);
    val('$' + fmtAmt(totalUSD), col3bX);
    y += 18;

    doc.setFillColor(237, 27, 36);
    doc.rect(margin, y, pageWidth - 2 * margin, 15, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('TOTAL PKR', margin + 5, y + 9.5);
    doc.setFontSize(14);
    doc.text('PKR ' + fmtAmt(totalPKR), pageWidth - margin - 5, y + 10, { align: 'right' });
    doc.setTextColor(40, 40, 40);
    contentEndY = y + 15;
  } else {
    const items = Array.isArray(inv.items) ? inv.items : [];
    autoTable(doc, {
      startY: tableStartY,
      head: [['#', 'Description', 'Qty', 'UOM', 'Unit Price', 'Amount (PKR)']],
      body: items.map((it, i) => [
        i + 1,
        String(it.description || '-').toUpperCase(),
        String(it.quantity || 0),
        (it.uom || 'pcs').toUpperCase(),
        fmtAmt(amount(it.unit_price)),
        fmtAmt(amount(it.quantity) * amount(it.unit_price)),
      ]),
      margin: { left: margin, right: margin },
      theme: 'plain',
      headStyles: { fillColor: [237, 27, 36], textColor: [255, 255, 255], fontSize: 10, fontStyle: 'bold', cellPadding: 1, halign: 'center' },
      bodyStyles: { fontSize: 10, cellPadding: 0.8, textColor: [40, 40, 40], halign: 'center' },
      alternateRowStyles: { fillColor: [248, 249, 252] },
      styles: { lineColor: [220, 220, 220], lineWidth: 0.2 },
      columnStyles: {
        0: { cellWidth: 12, halign: 'center' },
        2: { halign: 'center', cellWidth: 16 },
        3: { halign: 'center', cellWidth: 18 },
        4: { halign: 'center' },
        5: { halign: 'right' },
      },
      didParseCell: (data) => {
        if (data.section === 'head' && data.column.index === 5) {
          data.cell.styles.halign = 'right';
        }
      },
    });

    const afterTableY = doc.lastAutoTable.finalY + 4;
    const summaryRows = [];
    summaryRows.push([{ content: 'Subtotal', styles: { fontStyle: 'normal', textColor: [80, 80, 80] } }, { content: 'PKR ' + fmtAmt(inv.subtotal), styles: { halign: 'right' } }]);
    if (Number(inv.tax_rate) > 0) {
      summaryRows.push([{ content: 'Tax (' + (inv.tax_rate || 0) + '%)', styles: { textColor: [80, 80, 80] } }, { content: 'PKR ' + fmtAmt(inv.tax_amount), styles: { halign: 'right' } }]);
    }
    summaryRows.push([{ content: 'TOTAL', styles: { fontStyle: 'bold', fillColor: [237, 27, 36], textColor: [255, 255, 255], cellPadding: 1 } }, { content: 'PKR ' + fmtAmt(inv.total), styles: { halign: 'right', fontStyle: 'bold', fillColor: [237, 27, 36], textColor: [255, 255, 255], cellPadding: 1 } }]);
    if (items.length === 0) summaryRows.unshift([{ content: 'No items', colSpan: 2, styles: { halign: 'center', textColor: [150, 150, 150] } }]);

    autoTable(doc, {
      startY: afterTableY,
      body: summaryRows,
      theme: 'plain',
      margin: { left: pageWidth - 100, right: margin },
      styles: { fontSize: 10, cellPadding: 0.5, lineColor: [220, 220, 220], lineWidth: 0.2 },
      headStyles: { cellPadding: 1 },
      columnStyles: {
        0: { cellWidth: 42, halign: 'right' },
        1: { cellWidth: 44, halign: 'right' },
      },
    });
  }

  const wordsY = (contentEndY != null
    ? contentEndY
    : (doc.lastAutoTable ? doc.lastAutoTable.finalY : tableStartY)) + 10;
  const wordsBase = isCommission ? (amount(inv.total_pkr) || amount(inv.total)) : amount(inv.total);
  const totalForWords = wordsBase;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(23, 32, 51);
  doc.text('Amount in Words:', margin, wordsY);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(40, 40, 40);
  const words = numberToWords(totalForWords);
  const wordsLines = doc.splitTextToSize(words, pageWidth - 2 * margin);
  doc.text(wordsLines, margin, wordsY + 6);

  // Signature section at page bottom (just above footer) — line above labels, no "Created By"
  const footerLines = enrichedSettings.invoice_footer_text
    ? doc.splitTextToSize(enrichedSettings.invoice_footer_text, pageWidth - 2 * margin).length
    : 0;
  const sigY = pageHeight - 26 - footerLines * 4;
  if (!isCommission && wordsY + 6 + wordsLines.length * 5 > sigY - 8) {
    doc.addPage();
  }
  const sigColWidth = (pageWidth - 2 * margin) / 4;
  if (!isCommission) {
    [{ label: 'Prepared & Chequed By' }, { label: 'Manage Finance' }, { label: 'Authorized By' }, { label: 'Receiver Signature' }].forEach((sig, i) => {
      const x = margin + i * sigColWidth;
      doc.setDrawColor(60, 60, 60);
      doc.setLineWidth(0.3);
      doc.line(x, sigY, x + sigColWidth - 6, sigY);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(40, 40, 40);
      doc.text(sig.label, x, sigY + 6);
    });
  }

  addFooter(doc, pageWidth, pageHeight, margin, enrichedSettings);

  const customer = (party.name || 'customer').replace(/[^a-z0-9]+/gi, '_');
  const stamp = fileStamp();
  if (print) {
    printDoc(doc);
  } else {
    doc.save(customer + '_' + stamp + '.pdf');
  }
};

export const exportToPDF = (rows, reportName, columns, range = null, mainHeading = null, summary = null, accountTitle = null) => {
  const data = columns && columns.length
    ? (Array.isArray(rows) ? rows : toArray(rows)).map((r) =>
        pick(r, columns.map((c) => c.key))
      )
    : toArray(rows);
  const header = columns && columns.length ? columns.map((c) => c.label || c.key) : [];

  const isLedger = reportName === 'ledger_report';
  const doc = new jsPDF();
  doc.setFillColor(23, 32, 51);
  doc.rect(0, 0, doc.internal.pageSize.getWidth(), 26, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text((isLedger ? (mainHeading || COMPANY_NAME) : reportName.replace(/_/g, ' ')).toUpperCase(), 14, 12);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(fileStamp().replace(/_/g, ' '), doc.internal.pageSize.getWidth() - 14, 12, { align: 'right' });
  if (range) {
    doc.setFontSize(8);
    const fromTo = `From: ${range.from ? fmtDate(range.from) : '-'}   To: ${range.to ? fmtDate(range.to) : '-'}`;
    doc.text(fromTo, 14, 21);
  }
  doc.setTextColor(40, 40, 40);
  let startY = 32;
  if (isLedger) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('LEDGER REPORT', doc.internal.pageSize.getWidth() / 2, 36, { align: 'center' });
    if (accountTitle) {
      doc.setFontSize(9);
      doc.text(`Account: ${accountTitle}`, 14, 36);
    }
    if (summary) {
      doc.setFontSize(9);
      doc.text(`Opening Balance: ${formatMoney(summary.opening_balance)}`, doc.internal.pageSize.getWidth() - 14, 36, { align: 'right' });
    }
    startY = 46;
  }

  const bodyRows = header.length
    ? data.map((r) => columns.map((c) => pdfCell(c.key, r[c.key])))
    : data.map((r) => Object.entries(r).map(([k, v]) => pdfCell(k, v)));

  if (summary && header.length) {
    const totalRow = columns.map((c) => {
      if (c.key === 'date') return 'TOTAL';
      if (c.key === 'debit') return pdfCell('debit', summary.total_debit || 0);
      if (c.key === 'credit') return pdfCell('credit', summary.total_credit || 0);
      if (c.key === 'balance') return pdfCell('balance', summary.closing_balance || 0);
      return '';
    });
    bodyRows.push(totalRow);
  }

  autoTable(doc, {
    startY,
    head: header.length ? [header] : undefined,
    body: bodyRows,
    margin: { left: 14, right: 14 },
    theme: 'striped',
    headStyles: { fillColor: [23, 32, 51], fontSize: 9 },
    styles: { fontSize: 9 },
    ...(summary ? { foot: undefined } : {}),
  });

  doc.save(`${reportName}_${fileStamp()}.pdf`);
};

export const gatepassToPDF = async (gatepass, companyName, settings = {}, copyOverride = null, print = false) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const g = gatepass || {};
  const items = g.items || [];
  const company = g.party?.name || g.other_party_name || '—';

  const resolved = {
    ...settings,
    gatepass_header_name: companyName || '',
    gatepass_header_phone: '+93 307 1119988',
    gatepass_header_email: 'info@ahmed-corporation.com',
    gatepass_header_website: 'www.ahmed-corporation.com',
    gatepass_header_gst: '3277876175600',
    gatepass_header_ntn: '6380346-3',
    gatepass_logo_base64: settings.gatepass_logo_base64 || '',
    gatepass_footer_text: settings.gatepass_footer_text || '',
  };

  const logoData = await loadLogo(resolved.gatepass_logo_base64);
  const title = g.type === 'IN' ? 'MATERIAL IN GATEPASS' : 'MATERIAL OUT GATEPASS';

  // Draws one complete gatepass into a region of the page.
  // target = jsPDF to draw on (scratch doc during fit-check, real doc after),
  // y0     = region origin (0 → top half of the A4),
  // s      = vertical position scale (0.5 → half page),
  // f      = font scale (1 → requested sizes, shrunk if content overflows).
  const draw = (target, y0, s, copyLabel, f) => {
    const m = s === 1 ? 14 : 10;
    const xx = (bx) => bx + (m - 14);
    const yy = (by) => y0 + by * s;
    const w = (bw) => bw + (m - 14);
    const rowPitch = 4.3 * f + 0.35;

    // Company header (logo/name + contacts)
    let logoBottomY = 10;
    if (logoData) {
      const ratio = Math.min(70 / logoData.w, 40 / logoData.h);
      target.addImage(logoData.data, 'PNG', xx(14), yy(10), logoData.w * ratio * s, logoData.h * ratio * s);
      logoBottomY = 10 + logoData.h * ratio;
    } else {
      target.setFontSize(14 * f);
      target.setFont('helvetica', 'bold');
      target.setTextColor(23, 32, 51);
      target.text((resolved.gatepass_header_name || companyName || '').toUpperCase(), xx(14), yy(18));
      logoBottomY = 18;
    }
    target.setFontSize(7 * f);
    target.setFont('helvetica', 'normal');
    target.setTextColor(80, 80, 80);
    const contacts = [];
    if (resolved.gatepass_header_phone) contacts.push('Tel: ' + resolved.gatepass_header_phone);
    if (resolved.gatepass_header_email) contacts.push('Email: ' + resolved.gatepass_header_email);
    if (resolved.gatepass_header_website) contacts.push('Web: ' + resolved.gatepass_header_website);
    if (resolved.gatepass_header_gst) contacts.push('GST # ' + resolved.gatepass_header_gst);
    if (resolved.gatepass_header_ntn) contacts.push('NTN # ' + resolved.gatepass_header_ntn);
    const contactPitch = 3 * f + 0.6;
    let contactY = yy(10);
    contacts.forEach((line) => {
      target.text(line, pageWidth - m, contactY, { align: 'right' });
      contactY += contactPitch;
    });
    const contactsBottomPhys = contacts.length ? contactY - contactPitch + 1.8 : 0;
    const headBottomPhys = Math.max(yy(logoBottomY), contactsBottomPhys) + 2;
    target.setDrawColor(200, 200, 200);
    target.setLineWidth(0.3);
    target.line(m, headBottomPhys, pageWidth - m, headBottomPhys);
    const startY = headBottomPhys + 3;

    // Title + copy label
    target.setFontSize(12 * f);
    target.setFont('helvetica', 'bold');
    target.setTextColor(23, 32, 51);
    target.text(title, pageWidth / 2, startY + 6 * s, { align: 'center' });

    target.setFontSize(8 * f);
    target.setFont('helvetica', 'bold');
    target.setTextColor(120, 120, 120);
    target.text(copyLabel, pageWidth / 2, startY + 12 * s, { align: 'center' });

    let curY = startY + 18 * s;
    const labelX = xx(pageWidth / 2 + 20);
    const valueX = xx(pageWidth / 2 + 55);
    target.setFontSize(10 * f);

    // Right column: Gatepass No / Type / Date / Vehicle No — always shown
    let rightY = curY;
    [
      ['Gatepass No', g.gatepass_no || '-'],
      ['Type', g.type === 'IN' ? 'Material In' : 'Material Out'],
      ['Date', g.date ? fmtDate(g.date) : '-'],
      ['Vehicle No', g.vehicle_no || '-'],
    ].forEach(([label, value]) => {
      target.setFont('helvetica', 'normal');
      target.setTextColor(120, 120, 120);
      target.text(label, labelX, rightY);
      target.setTextColor(0, 0, 0);
      target.text(String(value), valueX, rightY);
      rightY += rowPitch;
    });

    // Left column: party/company, person, receiver, transporter, returnable, notes
    let leftY = curY;
    const companyLines = target.splitTextToSize(company, w(pageWidth / 2 - 20));
    target.setFont('helvetica', 'bold');
    target.setTextColor(0, 0, 0);
    target.text(companyLines, xx(14), leftY);
    leftY += companyLines.length * rowPitch;

    const leftRow = (label, valueText, bx) => {
      target.setFont('helvetica', 'normal');
      target.setTextColor(120, 120, 120);
      target.text(label, xx(14), leftY);
      target.setTextColor(0, 0, 0);
      const lines = target.splitTextToSize(' ' + valueText, w(pageWidth / 2 - 4 - bx));
      target.text(lines, xx(bx), leftY);
      leftY += lines.length * rowPitch;
    };
    if (g.person_name) leftRow('Person:', g.person_name, 34);
    if (g.receiver_name) leftRow('Receiver:', g.receiver_name, 34);
    if (g.transporter) leftRow('Transporter:', g.transporter, 44);
    leftRow('Returnable:', g.is_returnable ? 'Returnable' : 'Non-Returnable', 44);
    if (g.notes) {
      target.setFont('helvetica', 'normal');
      target.setTextColor(120, 120, 120);
      target.text('Notes:', xx(14), leftY);
      target.setTextColor(0, 0, 0);
      const notesLines = target.splitTextToSize(' ' + g.notes, w(pageWidth - 44));
      target.text(notesLines, xx(30), leftY);
      leftY += notesLines.length * rowPitch;
    }

    const tableStartY = Math.max(leftY, rightY) + 3 * s;

    autoTable(target, {
      startY: tableStartY,
      head: [['#', 'Article No', 'Name', 'Unit', 'Quantity', 'Remarks']],
      body: items.map((item, i) => [
        i + 1,
        item.article_no || '—',
        item.name || '—',
        item.unit || '—',
        String(Number(item.quantity)),
        item.remarks || '—',
      ]),
      margin: { left: m, right: m },
      theme: 'plain',
      headStyles: { fillColor: [237, 27, 36], textColor: [255, 255, 255], fontSize: 10 * f, fontStyle: 'bold', cellPadding: 0.7, halign: 'center' },
      bodyStyles: { fontSize: 10 * f, cellPadding: 0.5, textColor: [40, 40, 40], halign: 'center' },
      alternateRowStyles: { fillColor: [248, 249, 252] },
      styles: { lineColor: [220, 220, 220], lineWidth: 0.2 },
      columnStyles: {
        0: { cellWidth: 12, halign: 'center' },
        2: { halign: 'left' },
        4: { halign: 'center', cellWidth: 22 },
        5: { halign: 'left' },
      },
    });

    // Signature block: ~25mm gap above the line for easy signing
    target.setFontSize(7 * f);
    target.setFont('helvetica', 'normal');
    const gLines = resolved.gatepass_footer_text
      ? target.splitTextToSize(resolved.gatepass_footer_text, w(pageWidth - 28))
      : [];
    const regionBottom = y0 + pageHeight / 2;
    const tableEnd = target.lastAutoTable.finalY;
    const sigOffset = 3.5 * f + 1 * s;
    // Footer sits right below the signature labels (no big gap above the address).
    const footFromSig = sigOffset + 5;
    const footCap = regionBottom - 3 - (4 * f + 0.5) - Math.max(0, gLines.length - 1) * 3.6 * f;
    let sigY = tableEnd + 25;
    if (sigY + footFromSig > footCap) sigY = footCap - footFromSig;
    if (sigY < tableEnd + 8) sigY = tableEnd + 8;
    const footLimit = Math.min(sigY + footFromSig, footCap);

    const sigCol = (pageWidth - 28) / 3;
    const sigs = [
      { label: 'Recipient', value: '' },
      { label: 'Created By', value: g.creator?.name || '' },
      { label: 'Authorized Officer', value: '' },
    ];
    sigs.forEach((sig, i) => {
      const x = xx(14 + i * sigCol);
      if (sig.value) {
        target.setFontSize(10 * f);
        target.setFont('helvetica', 'normal');
        target.setTextColor(40, 40, 40);
        target.text(sig.value, x, sigY - sigOffset);
      }
      target.setDrawColor(60, 60, 60);
      target.setLineWidth(0.3);
      target.line(x, sigY, x + sigCol - 16, sigY);
      target.setFontSize(10 * f);
      target.setFont('helvetica', 'bold');
      target.setTextColor(40, 40, 40);
      target.text(sig.label, x, sigY + sigOffset);
    });

    // Separator line + footer below the signature block
    target.setFontSize(7 * f);
    target.setFont('helvetica', 'normal');
    target.setTextColor(100, 100, 100);
    target.setDrawColor(200, 200, 200);
    target.setLineWidth(0.2);
    target.line(m, footLimit, pageWidth - m, footLimit);
    gLines.forEach((line, i) => {
      target.text(line, pageWidth / 2, footLimit + 4 * f + 0.5 + i * 3.6 * f, { align: 'center' });
    });
    target.setTextColor(40, 40, 40);

    return { tableEnd, footLimit, sigY };
  };

  // The WHOLE gatepass (header → footer) is drawn inside the TOP HALF of the
  // A4 page; the user combines/prints 2 per sheet at print time.
  const copyType = copyOverride || g.copy_type || 'original';
  const copyLabel = (copyType === 'duplicate' ? 'Duplicate' : 'Original').toUpperCase();

  // Font size 10 requested; shrink only if the table would overflow the half.
  let chosenF = 0.55;
  [1, 0.85, 0.72, 0.62, 0.55].some((f) => {
    const scratch = new jsPDF();
    const metrics = draw(scratch, 0, 0.5, copyLabel, f);
    if (metrics.sigY - metrics.tableEnd >= 25) {
      chosenF = f;
      return true;
    }
    return false;
  });
  draw(doc, 0, 0.5, copyLabel, chosenF);

  const stamp = fileStamp();
  if (print) {
    printDoc(doc);
  } else {
    doc.save(`${g.gatepass_no || 'gatepass'}_${stamp}.pdf`);
  }
};

// Voucher PDF (detail page Download/Print) — Voucher No / Paid To / Date info,
// Chq Slip | Chq Date | Head | Description | Debit | Credit table, Total row,
// Amount in Words and the standard footer.
export const voucherToPDF = async (voucher, companyName, settings = {}, print = false) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 14;
  const v = voucher || {};
  const entries = v.entries || [];

  const TYPE_LABEL = {
    general_voucher: 'General Voucher',
    bank_payment: 'Bank Payment',
    bank_receipt: 'Bank Receipt',
    cash_payment: 'Cash Payment',
    cash_receipt: 'Cash Receipt',
  };

  const footerText = settings.invoice_footer_text || settings.sale_footer_text || '';
  const logoData = await loadLogo(settings.invoice_logo_base64 || settings.sale_logo_base64 || '');
  const header = {
    ...settings,
    invoice_header_name: settings.sale_header_name || settings.invoice_header_name || companyName,
    invoice_header_phone: settings.sale_header_phone || settings.invoice_header_phone || '',
    invoice_header_address: settings.sale_header_address || settings.invoice_header_address || '',
    invoice_header_email: settings.sale_header_email || settings.invoice_header_email || '',
    invoice_header_website: settings.sale_header_website || settings.invoice_header_website || '',
    invoice_header_gst: settings.sale_header_gst || settings.invoice_header_gst || '',
    invoice_header_ntn: settings.sale_header_ntn || settings.invoice_header_ntn || '',
    invoice_footer_text: footerText,
    _logoData: logoData,
  };

  let y = addHeader(doc, pageWidth, margin, header, companyName);

  const typeSlug = v.voucher_type || (v.type && v.type.slug) || '';
  const typeLabel = (v.type && v.type.name) || TYPE_LABEL[typeSlug] || 'Voucher';
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(23, 32, 51);
  doc.text(typeLabel.toUpperCase(), pageWidth / 2, y + 4, { align: 'center' });
  y += 14;

  const partyEntry = entries.find((e) => e.party && e.party.name);
  const info = [
    ['Voucher No :', v.voucher_no || '-'],
    ['Paid To :', partyEntry ? partyEntry.party.name : '-'],
    ['Voucher Date :', fmtDate(v.date) || '-'],
  ];
  info.forEach(([label, value], i) => {
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(80, 80, 80);
    doc.text(label, margin, y + i * 6);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(40, 40, 40);
    doc.text(String(value), margin + 36, y + i * 6);
  });
  y += info.length * 6 + 6;

  const totalDebit = Number(v.total_debit) || entries.reduce((s, e) => s + amount(e.debit), 0);
  const totalCredit = Number(v.total_credit) || entries.reduce((s, e) => s + amount(e.credit), 0);

  autoTable(doc, {
    startY: y,
    head: [['Chq Slip', 'Chq Date', 'Head', 'Description', 'Debit', 'Credit']],
    body: [
      ...entries.map((e) => [
        '',
        '',
        (e.account && e.account.name) || '',
        e.narration || '',
        fmtAmt(e.debit),
        fmtAmt(e.credit),
      ]),
      [
        '', '', '',
        { content: 'Total', styles: { fontStyle: 'bold', halign: 'right' } },
        { content: fmtAmt(totalDebit), styles: { fontStyle: 'bold', halign: 'right' } },
        { content: fmtAmt(totalCredit), styles: { fontStyle: 'bold', halign: 'right' } },
      ],
    ],
    headStyles: { fillColor: [237, 27, 36], textColor: [255, 255, 255], fontSize: 10, cellPadding: 1 },
    styles: { fontSize: 10, cellPadding: 1 },
    columnStyles: {
      0: { cellWidth: 18 },
      1: { cellWidth: 18 },
      4: { halign: 'right' },
      5: { halign: 'right' },
    },
    margin: { left: margin, right: margin },
    theme: 'grid',
  });

  const wordsY = doc.lastAutoTable.finalY + 8;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(23, 32, 51);
  doc.text('Amount in Words:', margin, wordsY);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(40, 40, 40);
  const words = numberToWords(totalDebit).replace(/ Only$/, ' Rupees Only');
  const wordsLines = doc.splitTextToSize(words, pageWidth - 2 * margin);
  doc.text(wordsLines, margin, wordsY + 6);

  // 4 signatures in a single row, just above the footer.
  const footerLines = header.invoice_footer_text
    ? doc.splitTextToSize(header.invoice_footer_text, pageWidth - 2 * margin).length
    : 0;
  const sigY = pageHeight - 26 - footerLines * 4;
  if (wordsY + 6 + wordsLines.length * 5 > sigY - 8) {
    doc.addPage();
  }
  const sigColWidth = (pageWidth - 2 * margin) / 4;
  [{ label: 'Prepared & Chequed By' }, { label: 'Manage Finance' }, { label: 'Authorized By' }, { label: 'Receiver Signature' }].forEach((sig, i) => {
    const x = margin + i * sigColWidth;
    doc.setDrawColor(60, 60, 60);
    doc.setLineWidth(0.3);
    doc.line(x, sigY, x + sigColWidth - 6, sigY);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(40, 40, 40);
    doc.text(sig.label, x, sigY + 6);
  });

  addFooter(doc, pageWidth, pageHeight, margin, header);

  const stamp = fileStamp();
  if (print) {
    printDoc(doc);
  } else {
    doc.save(`${v.voucher_no || 'voucher'}_${stamp}.pdf`);
  }
};